import * as React from 'react';
import { FC, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import DesktopWindowsOutlinedIcon from '@mui/icons-material/DesktopWindowsOutlined';
import CustomDBDiscontinueLicenseButton from './CustomDBDiscontinueLicenseButton';

interface Order {
    tilausnumero: string;
    tilausmaara?: number | string;
    keskeytyksia?: number | string;
}

interface Props {
    resource_guid: string;
    possible_orders: Order[];
}

// Single icon that reveals the per-order discontinue buttons on hover.
const CustomDiscontinueLicenseMenu: FC<Props> = ({
    resource_guid,
    possible_orders,
}) => {
    const [open, setOpen] = useState(false);
    const anchorRef = useRef<HTMLSpanElement>(null);
    const closeTimer = useRef<ReturnType<typeof setTimeout>>();

    const handleOpen = () => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
        setOpen(true);
    };

    const handleClose = () => {
        closeTimer.current = setTimeout(() => setOpen(false), 120);
    };

    return (
        <Box
            component="span"
            ref={anchorRef}
            onMouseEnter={handleOpen}
            onMouseLeave={handleClose}
            sx={{ display: 'inline-flex' }}
        >
            <Button color="success" sx={{ minWidth: 0 }}>
                <DesktopWindowsOutlinedIcon />
            </Button>
            <Popover
                open={open}
                anchorEl={anchorRef.current}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                onClose={handleClose}
                disableRestoreFocus
                sx={{ pointerEvents: 'none' }}
                slotProps={{
                    paper: {
                        onMouseEnter: handleOpen,
                        onMouseLeave: handleClose,
                        sx: { pointerEvents: 'auto', p: 1 },
                    },
                }}
            >
                <Typography
                    variant="caption"
                    sx={{
                        display: 'block',
                        px: 1,
                        pb: 0.5,
                        fontWeight: 'bold',
                    }}
                >
                    Keskeytä lisenssi tilaukseen:
                </Typography>
                {possible_orders.map(order => {
                    const tilausmaara = Number(order.tilausmaara) || 0;
                    const keskeytyksia = Number(order.keskeytyksia) || 0;
                    const fullyDiscontinued = keskeytyksia >= tilausmaara;
                    return (
                        <Box
                            key={order.tilausnumero}
                            sx={{ display: 'flex', alignItems: 'center' }}
                        >
                            <CustomDBDiscontinueLicenseButton
                                resource_guid={resource_guid}
                                tilausnumero={order.tilausnumero}
                                buttonLabel={`${order.tilausnumero} (${keskeytyksia}/${tilausmaara} kpl)`}
                                size="small"
                                disabled={fullyDiscontinued}
                            />
                        </Box>
                    );
                })}
            </Popover>
        </Box>
    );
};

export default CustomDiscontinueLicenseMenu;
